/**
 * NextContactDialog (KAN-960)
 *
 * Модалка "Следующий контакт": вид (Звонок / Личная встреча), дата и время по
 * поясу организации (не в прошлом), заметка (необязательно). Владельцу и
 * оператору без менеджера сессии ещё выбор менеджера (как в окне "Назначить в
 * работу", AttentionDoneDialog); менеджер берёт клиента себе без выбора
 * (правило useNextContactManager). Сохранение записывает контакт вместо
 * прежнего и берёт клиента в работу; ошибка проверки или сервера видна в модалке.
 * Текущий шаг клиента виден строкой "Сейчас: <подпись>, <время>" (вид и время по
 * умолчанию из него: встреча остаётся встречей, шаг бота - "Звонок"); у
 * существующего шага кнопка "Убрать контакт" (без взятия в работу).
 */
import { useState } from 'react';
import { CalendarClock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ManagerSelect } from '@/components/managers/ManagerSelect';
import { NO_MANAGER_OPTION } from '@/components/dashboard/AttentionDoneDialog';
import { useAssignableManagers } from '@/hooks/useManagers';
import { useClearNextContact, useNextContactManager, useSetNextContact } from '@/hooks/useNextContact';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';
import { formatNextContact } from '@/lib/crmBoard';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import {
  NEXT_CONTACT_CLEAR_LABEL,
  NEXT_CONTACT_FAILED,
  NEXT_CONTACT_LABEL,
  NEXT_CONTACT_TYPES,
  checkContactTime,
  initialContactInput,
  initialContactType,
  type NextContactTarget,
  type NextContactType,
} from '@/lib/nextContact';
import { isoToZonedInput, orgTimezoneLabel } from '@/lib/orgTimezone';
import type { WorkSurface } from '@/lib/workActions';
import type { NextContactBody } from '@/services/api/nextContact';
import type { ManagerBrief } from '@/types/manager';

interface NextContactDialogProps {
  target: NextContactTarget;
  onClose: () => void;
  surface?: WorkSurface;
}

export function NextContactDialog({ target, onClose, surface = 'cabinet' }: NextContactDialogProps) {
  const timeZone = useOrgTimezone();
  const rule = useNextContactManager(surface);
  const save = useSetNextContact(target.clientId);
  const clear = useClearNextContact(target.clientId);
  const busy = save.isPending || clear.isPending;
  // Текущий шаг на карточке: подпись "Сейчас: ..." и кнопка "Убрать контакт".
  const currentAt = formatNextContact(target.followupAt, timeZone);
  const hasStep = !!target.followupLabel;
  const { data: managers } = useAssignableManagers();
  const [stepType, setStepType] = useState<NextContactType>(() => initialContactType(target.followupType));
  const [at, setAt] = useState(() => initialContactInput(target.followupAt, timeZone));
  const [note, setNote] = useState('');
  const [manager, setManager] = useState<ManagerBrief | null>(target.manager);
  const [error, setError] = useState<string | null>(null);

  const selectManager = (managerId: string | null) => {
    setManager(managerId === null ? null : (managers.find((m) => m.id === managerId) ?? null));
  };

  const submit = () => {
    const time = checkContactTime(at, timeZone);
    if ('error' in time) {
      setError(time.error);
      return;
    }
    setError(null);
    const body: NextContactBody = { step_type: stepType, at: time.iso, note: note.trim() };
    if (rule.choose) body.manager_id = manager?.id ?? null;
    else if (rule.fixedManagerId !== undefined) body.manager_id = rule.fixedManagerId;
    save.mutate(body, {
      onSuccess: onClose,
      onError: (err) => setError(extractErrorMessage(err, NEXT_CONTACT_FAILED)),
    });
  };

  const remove = () => {
    setError(null);
    clear.mutate(undefined, {
      onSuccess: onClose,
      onError: (err) => setError(extractErrorMessage(err, NEXT_CONTACT_FAILED)),
    });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-md" data-testid="next-contact-dialog">
        <DialogHeader>
          <DialogTitle>
            {NEXT_CONTACT_LABEL}: {target.name || 'Клиент'}
          </DialogTitle>
          <DialogDescription>Контакт заменит прежний, клиент будет взят в работу.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {currentAt && (
            <p className="text-sm text-gray-600" data-testid="next-contact-current">
              Сейчас: {[target.followupLabel, currentAt].filter(Boolean).join(', ')}
            </p>
          )}
          <div className="flex gap-2" role="group" aria-label="Вид контакта">
            {NEXT_CONTACT_TYPES.map((type) => (
              <Button
                key={type.value}
                type="button"
                size="sm"
                variant={stepType === type.value ? 'default' : 'outline'}
                aria-pressed={stepType === type.value}
                className="flex-1"
                onClick={() => setStepType(type.value)}
              >
                {type.label}
              </Button>
            ))}
          </div>
          <label className="block space-y-1 text-sm text-gray-500">
            <span>Дата и время, {orgTimezoneLabel(timeZone)}</span>
            <Input
              type="datetime-local"
              value={at}
              min={isoToZonedInput(new Date().toISOString(), timeZone)}
              onChange={(e) => setAt(e.target.value)}
              data-testid="next-contact-at"
            />
          </label>
          <label className="block space-y-1 text-sm text-gray-500">
            <span>Заметка (необязательно)</span>
            <Textarea
              value={note}
              rows={2}
              maxLength={255}
              onChange={(e) => setNote(e.target.value)}
              data-testid="next-contact-note"
            />
          </label>
          {rule.choose && (
            <div className="space-y-1">
              <span className="text-sm text-gray-500">Менеджер</span>
              <ManagerSelect
                value={manager}
                onSelect={selectManager}
                noneLabel={NO_MANAGER_OPTION}
                testId="next-contact-manager"
              />
            </div>
          )}
          {error && (
            <p className="text-sm text-red-700" role="alert">
              {error}
            </p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          {hasStep && (
            <Button
              type="button"
              variant="outline"
              className="sm:mr-auto"
              disabled={busy}
              onClick={remove}
              data-testid="next-contact-clear"
            >
              {NEXT_CONTACT_CLEAR_LABEL}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" data-testid="next-contact-save" disabled={busy} onClick={submit}>
            {save.isPending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <CalendarClock className="mr-1 h-3.5 w-3.5" />
            )}
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
